import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { TrainingVideo, VideoUploadOptions } from '@/types/videoAnnotations';
import { uploadTrainingVideo, updateVideoStatus } from '@/services/videoService';

export const useTrainingVideos = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['training-videos', user?.id],
    queryFn: async (): Promise<TrainingVideo[]> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('training_videos')
        .select(`
          *,
          uploader:profiles!uploader_id(full_name, username, profile_picture_url),
          subject:profiles!subject_id(full_name, username, profile_picture_url),
          reviewer:profiles!reviewer_id(full_name, username, profile_picture_url)
        `)
        .or(`uploader_id.eq.${user.id},subject_id.eq.${user.id},reviewer_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as TrainingVideo[];
    },
    enabled: !!user,
  });
};

export const useTrainingVideo = (videoId: string | undefined) => {
  return useQuery({
    queryKey: ['training-video', videoId],
    queryFn: async (): Promise<TrainingVideo> => {
      if (!videoId) throw new Error('No video ID');

      const { data, error } = await supabase
        .from('training_videos')
        .select(`
          *,
          uploader:profiles!uploader_id(full_name, username, profile_picture_url),
          subject:profiles!subject_id(full_name, username, profile_picture_url),
          reviewer:profiles!reviewer_id(full_name, username, profile_picture_url)
        `)
        .eq('id', videoId)
        .single();

      if (error) throw error;
      return data as TrainingVideo;
    },
    enabled: !!videoId,
  });
};

export const useUploadVideo = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (options: VideoUploadOptions) => uploadTrainingVideo(options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-videos', user?.id] });
    },
  });
};

export const useUpdateVideoStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ videoId, status }: { videoId: string; status: TrainingVideo['status'] }) =>
      updateVideoStatus(videoId, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['training-video', variables.videoId] });
      queryClient.invalidateQueries({ queryKey: ['training-videos'] });
    },
  });
};
